import {
  ComponentType,
  ForwardRefExoticComponent,
  RefAttributes,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import {
  ArrowUpIcon,
  DiffIcon,
  MaximizeIcon,
  PaperclipIcon,
  SquareIcon,
  XIcon,
} from "lucide-react";

import { Icon } from "@/components/atoms/Icon";
import { FieldReference } from "@/components/organisms/Field/FieldReferences";
import { ComposerRef } from "./ThreadInputComposer";
import { useContextHotkeys } from "@/hooks/useContextTree";

import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

type ThreadContextItem = {
  id: string;
  identifier: string;
  name: string;
  typename: string;
};

export interface ThreadBoxProps {
  // State
  mode: "edit" | "readonly";
  activeMessageContext: ThreadContextItem[];
  /**
   * Rendered inside the card above the composer, edge to edge (the thread's
   * pending changes tray), so the two read as one control.
   */
  header?: React.ReactNode;
  /**
   * First in its row of chips over what is typed: whose the chat is - a
   * business's, or the person's own - and, before its first message, where
   * it is made.
   */
  scope?: React.ReactNode;
  /**
   * Given, the composer writes across its whole width and keeps a row of its
   * own along its foot: this, then its buttons (the thread's page context).
   * ``null`` keeps that row with nothing in it; left out, there is none.
   */
  footer?: React.ReactNode;
  // Components
  ThreadComposerComponent: ForwardRefExoticComponent<
    {
      onSubmit: (
        promptArray: Array<{ text?: string; id?: string }>,
      ) => boolean | void;
      onEscape?: () => void;
    } & RefAttributes<ComposerRef>
  >;
  ThreadReferencePopperComponent: ComponentType<{
    children: React.ReactNode;
    isOpenInitial?: boolean;
    onAdd?: (option: any) => void;
  }>;
  ThreadTogglePopperComponent: ComponentType<{
    children: React.ReactNode;
  }>;

  // Event handlers
  onSubmit?: (
    promptArray: Array<{ text?: string; id?: string }>,
  ) => boolean | void;
  onEscape?: () => void;
  onRemoveReference?: (id: string) => void;
  onModeChange?: (mode: "edit" | "readonly") => void;
  onAddReference?: (reference: any) => void;
  onSelectThread?: (threadId: string) => void;
  onNewThread?: () => void;
  /**
   * Set while a reply is in flight: the send button becomes a stop button
   * that calls this.
   */
  onStop?: () => void;
  /** The stop has been requested and has not taken effect yet. */
  stopping?: boolean;
  /**
   * Opens this thread on its own page. Only a host that is not that page
   * passes it — the slide-out panel — so the button is absent everywhere else.
   */
  onExpand?: () => void;
  /** Opens the node panel onto the changes this thread has made. */
  onShowChanges?: () => void;

  // Props
  context?: {
    name: string;
    nodeId: string;
  }[];
  isMainThread?: boolean;
  showThreadToggle?: boolean;
  /**
   * Files dropped on the thread, sent with the message. One still uploading
   * shows as a placeholder and cannot be removed yet.
   */
  files?: Array<{ key: string; name: string; isUploading: boolean }>;
  onRemoveFile?: (key: string) => void;
}

export type ThreadBoxHandle = {
  submit: () => void;
  focus: () => void;
};

export const ThreadBox = forwardRef<ThreadBoxHandle, ThreadBoxProps>(
  function ThreadBox(
    {
      // State
      mode,
      activeMessageContext,
      header,
      scope,
      footer,
      // Components
      ThreadComposerComponent,
      ThreadReferencePopperComponent,
      ThreadTogglePopperComponent,

      // Event handlers
      onSubmit,
      onEscape,
      onRemoveReference,
      onModeChange,
      onAddReference,
      onNewThread,
      files = [],
      onRemoveFile,
      onStop,
      stopping = false,
      onExpand,
      onShowChanges,

      // Props
      context = [],
      isMainThread = false,
      showThreadToggle = true,
    }: ThreadBoxProps,
    ref,
  ) {
    const composerRef = useRef<ComposerRef>(null);

    // Read only again on a click or a touch away from it - as Material's
    // ``ClickAwayListener`` had it: once it has been on the page a moment,
    // not on the window's scrollbar, not after a touch that moved, and not
    // on anything it drew, on the page or elsewhere (a popper of its own,
    // which React tells it of as the click passes through it first).
    const boxRef = useRef<HTMLDivElement>(null);
    const isClickInsideReactTree = useRef(false);
    const clickAway = useRef(() => {});
    clickAway.current = () => {
      if (!isMainThread && onModeChange) {
        onModeChange("readonly");
      }
    };
    useEffect(() => {
      let isArmed = false;
      let isTouchMoved = false;
      const arm = setTimeout(() => {
        isArmed = true;
      }, 0);
      const away = (event: MouseEvent | TouchEvent) => {
        const isInsideReactTree = isClickInsideReactTree.current;
        isClickInsideReactTree.current = false;
        const box = boxRef.current;
        if (!isArmed || !box) return;
        if (
          "clientX" in event &&
          (document.documentElement.clientWidth < event.clientX ||
            document.documentElement.clientHeight < event.clientY)
        ) {
          return;
        }
        if (isTouchMoved) {
          isTouchMoved = false;
          return;
        }
        if (event.composedPath().includes(box) || isInsideReactTree) return;
        clickAway.current();
      };
      const touchMove = () => {
        isTouchMoved = true;
      };
      document.addEventListener("click", away);
      document.addEventListener("touchend", away);
      document.addEventListener("touchmove", touchMove);
      return () => {
        clearTimeout(arm);
        document.removeEventListener("click", away);
        document.removeEventListener("touchend", away);
        document.removeEventListener("touchmove", touchMove);
      };
    }, []);

    useImperativeHandle(ref, () => ({
      submit: () => composerRef.current?.submit(),
      focus: () => composerRef.current?.focus(),
    }));

    const handleSubmit = useCallback((event: KeyboardEvent) => {
      event.preventDefault();
      composerRef.current?.submit();
    }, []);

    useContextHotkeys({
      combos: { mac: "meta+enter", windows: "ctrl+enter", linux: "ctrl+enter" },
      callback: handleSubmit,
      id: "thread-submit",
    });

    const buttons = (
      <>
        {onShowChanges && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Show changes"
                onClick={(event) => {
                  event.stopPropagation();
                  onShowChanges();
                }}
                className="shrink-0 rounded-full"
              >
                <Icon ReactIcon={DiffIcon} size="md" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Changes in this thread
            </TooltipContent>
          </Tooltip>
        )}
        {onExpand && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="secondary"
                size="icon-sm"
                aria-label="Open in thread view"
                onClick={(event) => {
                  event.stopPropagation();
                  onExpand();
                }}
                className="shrink-0 rounded-full"
              >
                <Icon ReactIcon={MaximizeIcon} size="md" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Open in thread view</TooltipContent>
          </Tooltip>
        )}
        {onStop ? (
          <Button
            variant="destructive"
            size="icon-sm"
            aria-label="Stop"
            disabled={stopping}
            onClick={(event) => {
              event.stopPropagation();
              onStop();
            }}
            className="shrink-0 rounded-full"
          >
            {stopping ? (
              <Spinner />
            ) : (
              <Icon ReactIcon={SquareIcon} size="sm" fill="currentColor" />
            )}
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="icon-sm"
            aria-label="Submit"
            onClick={(event) => {
              event.stopPropagation();
              composerRef.current?.submit();
            }}
            className="shrink-0 rounded-full"
          >
            <Icon ReactIcon={ArrowUpIcon} size="md" />
          </Button>
        )}
      </>
    );

    return (
      <div
        ref={boxRef}
        data-testid="thread-input-box"
        onClick={() => {
          isClickInsideReactTree.current = true;
          if (!isMainThread && onModeChange) {
            onModeChange("edit");
          }
        }}
        onTouchEnd={() => {
          isClickInsideReactTree.current = true;
        }}
        className={cn(
          "flex text-center w-full bg-background rounded-xl items-start border-border flex-col [transition:all_0.15s_ease-in-out]",
          mode === "readonly" ? "cursor-pointer" : "cursor-default",
          "[border-width:1px] [border-style:solid] border-border shadow-sm",
        )}
      >
        {header}
        {/* The composer body. The padding lives here rather than on the
              card, so the header runs edge to edge, and so does the focus
              ring: the body sits over the card's border (-1px) and draws the
              ring as inset shadows, around the composer and not the header.
              Under a header its top corners are square. */}
        <div
          // A press on its own ground - not what is typed, a chip or a
          // button - keeps the keyboard in what is typed, so its ring does
          // not drop while the mouse is held down.
          onMouseDown={(event) => {
            const target = event.target as HTMLElement;
            if (
              target.closest(
                '[contenteditable="true"], button, a, input, textarea, [role="button"], [tabindex]:not([tabindex="-1"])',
              )
            ) {
              return;
            }
            event.preventDefault();
            composerRef.current?.focus();
          }}
          className={cn(
            "w-[calc(100%+2px)] -m-px flex flex-col items-start p-3 rounded-xl [&:not(:first-child)]:rounded-tl-none [&:not(:first-child)]:rounded-tr-none [transition:box-shadow_0.15s_ease-in-out]",
            // Or a control in it whose own popper has the keyboard - the
            // chat's scope picker - so the ring does not drop as it opens.
            "focus-within:[box-shadow:inset_0_0_0_1px_var(--primary),inset_0_0_0_4px_color-mix(in_srgb,var(--primary)_10%,transparent_90%)]",
            "[&:has([data-keeps-focus-ring])]:[box-shadow:inset_0_0_0_1px_var(--primary),inset_0_0_0_4px_color-mix(in_srgb,var(--primary)_10%,transparent_90%)]",
          )}
        >
          {(isMainThread ||
            scope != null ||
            (mode === "readonly" && context.length > 0) ||
            mode === "edit") && (
            <div
              data-testid="thread-message-context"
              className="w-full flex flex-row items-center justify-start flex-wrap gap-1 pb-1 border-border"
            >
              {scope}
              {activeMessageContext.map(
                ({ id, identifier, name, typename }) => (
                  <div key={id} className="flex items-center">
                    <FieldReference
                      key={id}
                      value={{
                        name: name,
                        id: id,
                        identifier: identifier,
                        __typename: typename,
                      }}
                      valueOptions={{
                        style: "related",
                        isNullable: true,
                      }}
                      isMultiline={false}
                      events={{
                        onDeleteField: () => {
                          onRemoveReference?.(id);
                        },
                      }}
                      style={{ paddingLeft: 0, paddingRight: 0 }}
                    />
                  </div>
                ),
              )}
              {files.map(({ key, name, isUploading }) =>
                isUploading ? (
                  <Skeleton
                    key={key}
                    variant="rectangular"
                    data-testid="thread-uploading-file"
                    aria-label={`Uploading ${name}`}
                    className="rounded-sm w-auto"
                  >
                    <Typography
                      level="body-xs"
                      className="truncate pl-2 pr-2 pt-0.5 pb-0.5"
                    >
                      {name}
                    </Typography>
                  </Skeleton>
                ) : (
                  <Badge
                    variant="secondary"
                    key={key}
                    data-testid="thread-attached-file"
                  >
                    <Icon ReactIcon={PaperclipIcon} size="sm" />
                    {name}
                    <button
                      type="button"
                      aria-label={`Remove ${name}`}
                      onClick={() => onRemoveFile?.(key)}
                      className="-mr-1 rounded-full p-0.5 hover:bg-foreground/10"
                    >
                      <XIcon className="size-3" />
                    </button>
                  </Badge>
                ),
              )}
            </div>
          )}

          {footer !== undefined ? (
            <>
              <div className="w-full">
                <ThreadComposerComponent
                  onSubmit={onSubmit}
                  onEscape={onEscape}
                  ref={composerRef}
                />
              </div>
              <div
                data-testid="thread-input-foot"
                className="w-full flex items-end gap-2 pt-1"
              >
                <div className="flex-1 min-w-0">{footer}</div>
                {buttons}
              </div>
            </>
          ) : (
            <div className="w-full flex items-end gap-2">
              <div className="flex-1 min-w-0">
                <ThreadComposerComponent
                  onSubmit={onSubmit}
                  onEscape={onEscape}
                  ref={composerRef}
                />
              </div>
              {buttons}
            </div>
          )}
        </div>
      </div>
    );
  },
);
